import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import AppShell from './components/AppShell';
import ErrorBoundary from './components/ErrorBoundary';
import { Button, EmptyState } from './components/primitives';
import Landing from './routes/Landing';
import NewReview from './routes/NewReview';
import Workspace from './routes/Workspace';
import CommentSheet from './routes/CommentSheet';
import Taxonomy from './routes/Taxonomy';
import Method from './routes/Method';
import Guide from './routes/Guide';
import { REDIRECTS, fillTarget } from './routes/redirects';

/**
 * One retired address. The history entry is replaced rather than pushed, so
 * Back from the new page goes where the reviewer came from rather than
 * bouncing off the redirect.
 */
function Redirect({ to }: { to: string }) {
  const params = useParams();
  return <Navigate to={fillTarget(to, params)} replace />;
}

export default function App() {
  return (
    <AppShell>
      <Routes>
        <Route
          path="/"
          element={
            <ErrorBoundary area="the landing page">
              <Landing />
            </ErrorBoundary>
          }
        />
        <Route
          path="/new"
          element={
            <ErrorBoundary area="the start-a-review page">
              <NewReview />
            </ErrorBoundary>
          }
        />
        <Route
          path="/review/:docId"
          element={
            <ErrorBoundary area="the review workspace">
              <Workspace />
            </ErrorBoundary>
          }
        />
        <Route
          path="/review/:docId/comments"
          element={
            <ErrorBoundary area="the comment sheet">
              <CommentSheet />
            </ErrorBoundary>
          }
        />
        <Route
          path="/checks"
          element={
            <ErrorBoundary area="the checks reference">
              <Taxonomy />
            </ErrorBoundary>
          }
        />
        <Route
          path="/method"
          element={
            <ErrorBoundary area="the method page">
              <Method />
            </ErrorBoundary>
          }
        />
        <Route
          path="/guide"
          element={
            <ErrorBoundary area="the guide">
              <Guide />
            </ErrorBoundary>
          }
        />

        {/* Every address this application used to answer on. See
            src/routes/redirects.ts. */}
        {REDIRECTS.map(({ from, to }) => (
          <Route key={from} path={from} element={<Redirect to={to} />} />
        ))}

        {/* A typo or a stale link. Anything but a blank page. */}
        <Route
          path="*"
          element={
            <div className="route-empty-wrap">
              <EmptyState
                heading="There is no page at that address"
                body="The link may be mistyped, or it may point at an older version of this tool. Nothing you have reviewed is affected — your documents stay in this browser."
                action={
                  <Button variant="primary" marker to="/">
                    Go to the start
                  </Button>
                }
              />
            </div>
          }
        />
      </Routes>
    </AppShell>
  );
}
